import { useMemo, useState } from "react";
import { Box, Colors } from "@analyticsMD/quil";
import { css } from "@emotion/css";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Link from "@mui/material/Link";
import CollapsibleSection from "../shared/collapsible-section";

import type { FC, ReactNode } from "react";
import type { PatientWeightHistory } from "../../types/Malnutrition";

const BLUE_ICON_BG = "#3F51B5";

const styles = {
  subCard: css`
    background: #ffffff;
    border-radius: 0.5rem;
    margin: 0 0.75rem 0.625rem;
  `,
  tableHead: css`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    padding: 0.5rem 0;
    border-bottom: 1px solid ${Colors.grey_20};
    color: ${Colors.grey_65};
    font-weight: 600;
    font-size: 0.8125rem;
  `,
  tableRow: css`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    padding: 0.5rem 0;
    font-size: 0.875rem;
    color: ${Colors.grey_80};
    align-items: center;
  `,
  anchorRow: css`
    background: #f4eef9;
    margin: 0 -0.75rem;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    position: relative;
    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0.25rem;
      bottom: 0.25rem;
      width: 3px;
      background: #6a3fa0;
      border-radius: 1px;
    }
  `,
  rangeSelector: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    color: ${Colors.grey_65};
    font-size: 0.8125rem;
  `,
  trendLine: css`
    color: #c62b2b;
    font-weight: 700;
    font-size: 0.9375rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-bottom: 0.5rem;
  `,
  showMore: css`
    display: inline-block;
    margin-top: 0.5rem;
    font-size: 0.875rem;
  `,
};

const sxStyles = {
  subHeading: {
    fontSize: "0.8125rem",
    fontWeight: 700,
    color: Colors.grey_65,
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
    display: "flex",
    alignItems: "center",
  },
  subIcon: {
    backgroundColor: BLUE_ICON_BG,
    color: Colors.white,
    borderRadius: "50%",
    padding: "0.25rem",
    fontSize: "1.125rem",
    marginRight: "0.5rem",
  },
};

const SubCardIcon: FC = () => <MapOutlinedIcon sx={sxStyles.subIcon} />;

const Trend: FC<{ percent: number; months: number }> = ({ percent, months }) => (
  <Box className={styles.trendLine}>
    <ArrowDownwardIcon sx={{ fontSize: "1rem" }} />
    {percent}% over ~{months} months
  </Box>
);

const TrendChip: FC<{ percent: number; months: number }> = ({ percent, months }) => (
  <Box
    component="span"
    sx={{
      color: "#c62b2b",
      fontWeight: 600,
      display: "inline-flex",
      alignItems: "center",
      gap: "0.25rem",
    }}
  >
    <ArrowDownwardIcon sx={{ fontSize: "0.9rem" }} />
    {percent} % over ~{months} months
  </Box>
);

interface RecentWeightsCardProps {
  history: PatientWeightHistory;
}

const RecentWeightsCard: FC<RecentWeightsCardProps> = ({ history }) => {
  const anchors = useMemo(
    () => history.readings.filter((r) => r.is_anchor),
    [history.readings]
  );
  const [fromAnchor, setFromAnchor] = useState(
    anchors[anchors.length - 1]?.date ?? history.readings[history.readings.length - 1]?.date
  );
  const [toAnchor, setToAnchor] = useState(
    anchors[0]?.date ?? history.readings[0]?.date
  );
  const [showAll, setShowAll] = useState(false);

  const previewCount = 6;
  const visible = showAll
    ? history.readings
    : history.readings.slice(0, previewCount);
  const hiddenCount = history.readings.length - previewCount;

  return (
    <CollapsibleSection
      defaultOpen
      title="RECENT WEIGHTS"
      icon={<SubCardIcon />}
      summary={<TrendChip percent={history.trend_percent} months={history.trend_months} />}
      headingTextSx={sxStyles.subHeading}
      outerClassName={styles.subCard}
    >
      <Trend percent={history.trend_percent} months={history.trend_months} />
      <Box className={styles.rangeSelector}>
        <span>FROM</span>
        <Select
          size="small"
          value={fromAnchor}
          onChange={(e) => setFromAnchor(String(e.target.value))}
          sx={{ fontSize: "0.8125rem", height: "1.75rem" }}
        >
          {anchors.map((a) => (
            <MenuItem key={a.date} value={a.date} sx={{ fontSize: "0.8125rem" }}>
              {a.weight_lb} lb · {a.date}
            </MenuItem>
          ))}
        </Select>
        <ArrowForwardIcon sx={{ fontSize: "0.875rem" }} />
        <span>TO</span>
        <Select
          size="small"
          value={toAnchor}
          onChange={(e) => setToAnchor(String(e.target.value))}
          sx={{ fontSize: "0.8125rem", height: "1.75rem" }}
        >
          {anchors.map((a) => (
            <MenuItem key={a.date} value={a.date} sx={{ fontSize: "0.8125rem" }}>
              {a.weight_lb} lb · {a.date}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box className={styles.tableHead}>
        <span>Date</span>
        <span>Weight</span>
        <span>Source</span>
      </Box>
      {visible.map((r) => {
        const isAnchorRow =
          r.is_anchor && (r.date === fromAnchor || r.date === toAnchor);
        return (
          <Box
            key={r.date}
            className={`${styles.tableRow}${isAnchorRow ? " " + styles.anchorRow : ""}`}
          >
            <span>{r.date}</span>
            <span>{r.weight_lb} lb</span>
            <span>{r.source}</span>
          </Box>
        );
      })}
      {!showAll && hiddenCount > 0 && (
        <Link
          component="button"
          underline="hover"
          onClick={() => setShowAll(true)}
          className={styles.showMore}
        >
          Show {hiddenCount} more
        </Link>
      )}
    </CollapsibleSection>
  );
};

interface SupportingDataSectionProps {
  weights: PatientWeightHistory;
}

const SupportingDataHeader: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      fontSize: "0.8125rem",
      fontWeight: 700,
      color: Colors.grey_65,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      padding: "0.75rem 1.5rem 0.5rem",
    }}
  >
    {children}
  </Box>
);

const SupportingDataSection: FC<SupportingDataSectionProps> = ({ weights }) => (
  <Box>
    <SupportingDataHeader>Supporting clinical data</SupportingDataHeader>
    <RecentWeightsCard history={weights} />
  </Box>
);

export default SupportingDataSection;
export { RecentWeightsCard };
